import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import { formatBytes } from "../../lib/format";

export default function DesktopDownload() {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        api.get("/desktop/releases").then(({ data }) => setInfo(data)).catch(() => setInfo(null));
    }, []);

    // Build the displayed download cards intelligently:
    // - If "Universal" mac is uploaded → show it (collapses Intel + Silicon into one card)
    // - Otherwise → show separated Intel + Silicon cards
    const buildPlatforms = () => {
        const macUniversal = info?.mac;
        const list = [];
        if (macUniversal?.available) {
            list.push({ key: "mac", label: "macOS", sub: "Universal · Intel + Apple Silicon", icon: <MacIcon /> });
        } else {
            list.push({ key: "mac_intel", label: "macOS Intel",         sub: "x86_64", icon: <MacIcon /> });
            list.push({ key: "mac_arm64", label: "macOS Apple Silicon", sub: "arm64",  icon: <MacIcon /> });
        }
        list.push({ key: "windows", label: "Windows", sub: "64-bit", icon: <WinIcon /> });
        return list;
    };
    const platforms = info ? buildPlatforms() : [
        { key: "mac", label: "macOS", sub: "Universal", icon: <MacIcon /> },
        { key: "windows", label: "Windows", sub: "64-bit", icon: <WinIcon /> },
    ];

    const resolveUrl = (raw) => {
        if (!raw) return "";
        if (raw.startsWith("/api/")) return `${BACKEND_URL}${raw}`;
        return raw;
    };

    return (
        <div className="min-h-screen flex flex-col" data-testid="desktop-page">
            <PublicHeader />

            <section className="relative overflow-hidden border-b border-avx-line">
                <div className="absolute inset-0 bg-avx-glow" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <div className="label-mono mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-avx-blue" />
                            Desktop client · v{info?.version || "1.0.0"}
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
                            Download <span className="text-avx-blue">AVX Pro</span> for your computer
                        </h1>
                        <p className="text-lg text-avx-muted leading-relaxed mb-4 max-w-2xl">
                            One single app to install, update and activate all your AVX Pro plugins. No need to visit the website every time.
                        </p>
                        {info?.release_notes && (
                            <p className="text-sm text-avx-text/80 italic mb-2 max-w-2xl">{info.release_notes}</p>
                        )}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 w-full">
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    {platforms.map((p) => {
                        const item = info?.[p.key];
                        const available = item?.available;
                        const href = resolveUrl(item?.url);
                        const formatSuffix = item?.format ? ` · .${item.format}` : "";
                        return (
                            <div key={p.key} className="panel-elevated p-8 flex flex-col" data-testid={`download-card-${p.key}`}>
                                <div className="text-avx-blue mb-6">{p.icon}</div>
                                <h2 className="font-heading font-black text-3xl tracking-tight mb-2">{p.label}</h2>
                                <div className="label-mono mb-6">{p.sub}{formatSuffix}</div>
                                {available ? (
                                    <>
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-primary px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm inline-block text-center"
                                            data-testid={`download-btn-${p.key}`}
                                        >
                                            Download for {p.label}
                                        </a>
                                        {item?.file_size > 0 && (
                                            <div className="text-[10px] font-mono text-avx-muted mt-3">
                                                {item.file_name} · {formatBytes(item.file_size)}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div>
                                        <div className="btn-secondary px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm text-center cursor-not-allowed opacity-60">
                                            Coming soon
                                        </div>
                                        <div className="text-[10px] font-mono text-avx-muted mt-3">
                                            The installer for {p.label} will be available very soon.
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    <Feature title="1-click install" desc="Click install and the app handles the rest: downloads, runs the installer and verifies." />
                    <Feature title="Per-machine activation" desc="Each license activates up to 2 machines. Release and reassign from the app or your web dashboard." />
                    <Feature title="Auto-update" desc="When we publish a new version, the app updates itself on launch." />
                </div>

                <div className="mt-16 panel p-8">
                    <div className="label-mono mb-3">/ Requirements</div>
                    <ul className="grid sm:grid-cols-2 gap-3 text-sm">
                        <li className="font-mono text-avx-text/90">· macOS 11+ (Big Sur or later)</li>
                        <li className="font-mono text-avx-text/90">· Windows 10/11 64-bit</li>
                        <li className="font-mono text-avx-text/90">· Internet connection for activation</li>
                        <li className="font-mono text-avx-text/90">· Active AVX Pro account</li>
                    </ul>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}

function Feature({ title, desc }) {
    return (
        <div className="panel p-5">
            <h3 className="font-heading font-bold text-base mb-1">{title}</h3>
            <p className="text-sm text-avx-muted leading-relaxed">{desc}</p>
        </div>
    );
}

function MacIcon() {
    return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.05 12.04c-.02-2.32 1.9-3.43 1.98-3.49-1.08-1.58-2.76-1.8-3.36-1.83-1.43-.14-2.79.84-3.52.84-.73 0-1.85-.82-3.04-.79-1.56.02-3 .91-3.81 2.31-1.62 2.81-.41 6.97 1.17 9.25.77 1.12 1.69 2.38 2.9 2.34 1.16-.05 1.6-.75 3-.75s1.79.75 3.02.73c1.25-.02 2.04-1.14 2.81-2.27.88-1.3 1.25-2.56 1.27-2.62-.03-.01-2.44-.94-2.46-3.72zm-2.31-6.85c.64-.78 1.07-1.86.95-2.94-.92.04-2.04.62-2.7 1.39-.59.69-1.11 1.79-.97 2.85 1.03.08 2.08-.52 2.72-1.3z" />
        </svg>
    );
}
function WinIcon() {
    return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 5.4 10.6 4.5v6.7H3V5.4zm0 7.3h7.6v6.8L3 18.6v-5.9zm8.4-8.4L21 3v8.2h-9.6V4.3zm0 8.4H21v8.4l-9.6-1.3v-7.1z" />
        </svg>
    );
}
